import { Button } from '@territory/ui';
import { WEDGE_ALPHA } from '@territory/tokens';
import { BRAND } from './wedgeBrand';
import { CSSProperties, useMemo, useState } from 'react';
import { TextField } from '@territory/ui';
import { useAuth } from '@territory/client/AuthContext';
import { API, apiUrl } from '@territory/client/routes';
import { Splash } from './Splash';
import { detectInAppBrowser, tryOpenInSystemBrowser } from './wedgeInAppBrowser';

/**
 * Internal-only grant console — wedge.repvantage.io/grant.
 *
 * A RepVantage team member opens this (typically from the link in a request
 * email), and generates a one-time access code for an approved outside
 * requester. The POST to /api/auth/wedge-access/grant is AuthGuard-protected
 * and the backend re-verifies the caller is on the internal domain, so an
 * external user who reaches this page cannot actually mint a code.
 *
 * This route is mounted OUTSIDE WedgeAuthGate (it must render for a signed-in
 * internal user without dropping them into PREP), so it owns its own auth
 * handling via useAuth().
 */
export function WedgeGrant() {
  const { isLoading, isAuthenticated } = useAuth();
  const params = new URLSearchParams(window.location.search);
  const [email, setEmail] = useState(params.get('email') || '');
  const [name, setName] = useState(params.get('name') || '');
  const [company, setCompany] = useState(params.get('company') || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState<{
    code: string;
    email: string;
    expiresInHours: number;
  } | null>(null);
  const [copied, setCopied] = useState(false);
  /* 2026-07-13 · in-app webview guard · a grant link tapped inside the
     Gmail-app webview would dead-end on Google's disallowed_useragent
     403. Steer to the system browser instead of redirecting. */
  const inAppInfo = useMemo(() => detectInAppBrowser(), []);
  const [escapeTried, setEscapeTried] = useState(false);

  const screen: CSSProperties = {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 14,
    padding: '32px 20px',
    textAlign: 'center',
    background:
      BRAND.screenBg,
    color: BRAND.text,
    fontFamily: BRAND.fontBody,
  };


  if (isLoading) return <Splash label="Checking your session" />;

  if (!isAuthenticated) {
    return (
      <div style={screen}>
        <img src="/wedge/repvantage-radar.svg?v=20260712" alt="RepVantage" width={56} height={56} />
        <div style={{ fontSize: 18, fontWeight: 700, color: BRAND.text }}>Grant console</div>
        <div style={{ fontSize: 14, color: BRAND.textSec, maxWidth: 320, lineHeight: 1.5 }}>
          Sign in with your @repvantage.io account to issue a PREP access code.
        </div>
        {inAppInfo.inApp && (
          <div
            style={{
              fontSize: 12.5,
              padding: '10px 12px',
              borderRadius: 10,
              background: BRAND.inAppNoticeSurface,
              border: `1px solid ${BRAND.inAppNoticeBorder}`,
              color: BRAND.warningControlInk,
              maxWidth: 320,
              lineHeight: 1.5,
            }}
          >
            You&rsquo;re inside an app&rsquo;s built-in browser · Google blocks sign-in
            here. {escapeTried
              ? 'If nothing opened, use the share menu and choose Open in Browser.'
              : 'The button below will try to open this page in your real browser.'}
          </div>
        )}
        <Button appearance="wedge-auth" authSize="compact" authMotion="instant" authHover="none"
          onClick={() => {
            if (inAppInfo.inApp) {
              tryOpenInSystemBrowser(inAppInfo);
              setEscapeTried(true);
              return;
            }
            window.location.assign(`${apiUrl(API.AUTH.GOOGLE)}?app=wedge`);
          }}
          style={{marginTop:6,width:'auto'}}
        >
          Sign in with Google
        </Button>
        <div style={{ fontSize: 12, color: BRAND.textMuted, maxWidth: 300, lineHeight: 1.5 }}>
          After signing in, reopen the grant link from the request email.
        </div>
      </div>
    );
  }

  const generate = async () => {
    setErr('');
    const e = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) {
      setErr('Enter a valid email address to grant.');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(apiUrl('/api/auth/wedge-access/grant'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: e, name: name.trim(), company: company.trim() }),
      });
      const j = (await res.json().catch(() => ({}))) as {
        code?: string;
        email?: string;
        expiresInDays?: number;
        expiresInHours?: number;
        message?: string;
      };
      if (!res.ok || !j.code) {
        throw new Error(j?.message || `Could not generate a code (${res.status}).`);
      }
      setResult({
        code: j.code,
        email: j.email || e,
        expiresInHours: j.expiresInHours ?? (j.expiresInDays ? j.expiresInDays * 24 : 48),
      });
      setCopied(false);
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'Could not generate a code.');
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <div style={screen}>
        <img src="/wedge/repvantage-radar.svg?v=20260712" alt="RepVantage" width={48} height={48} />
        <div style={{ fontSize: 13, color: BRAND.textSec }}>
          Access code for <b style={{ color: BRAND.text }}>{result.email}</b>
        </div>
        <div
          style={{
            fontSize: 30,
            fontWeight: 800,
            letterSpacing: 3,
            fontFamily: "'Share Tech Mono', ui-monospace, Menlo, monospace",
            color: BRAND.amber,
            background: WEDGE_ALPHA.amber10,
            border: `1px solid ${BRAND.warningControlBorder}`,
            borderRadius: 14,
            padding: '16px 24px',
          }}
        >
          {result.code}
        </div>
        <Button appearance="wedge-auth" variant="secondary" authSize="copy" authMotion="instant" authHover="none"
          onClick={() => {
            navigator.clipboard?.writeText(result.code).then(
              () => setCopied(true),
              () => setCopied(false),
            );
          }}
          style={{width:'auto'}}
        >
          {copied ? 'Copied' : 'Copy code'}
        </Button>
        <div style={{ fontSize: 13, color: BRAND.textSec, maxWidth: 320, lineHeight: 1.55 }}>
          Send this code to {result.email}. They enter it with their email at
          wedge.repvantage.io. Single-use, expires in {result.expiresInHours} hours,
          and grants <b style={{ color: BRAND.text }}>3 free PREP searches</b> over
          the next {result.expiresInHours}-hour window.
        </div>
        <Button appearance="wedge-auth" variant="ghost" authSize="plain-link" authMotion="instant" authHover="none"
          onClick={() => {
            setResult(null);
            setEmail('');
            setName('');
            setCompany('');
          }}
          style={{marginTop:4,width:'auto'}}
        >
          Grant another
        </Button>
      </div>
    );
  }

  return (
    <div style={screen}>
      <img src="/wedge/repvantage-radar.svg?v=20260712" alt="RepVantage" width={52} height={52} />
      <div style={{ fontSize: 18, fontWeight: 700, color: BRAND.text }}>Grant PREP access</div>
      <div style={{ fontSize: 13, color: BRAND.textSec, maxWidth: 320, lineHeight: 1.5 }}>
        Generate a one-time access code for an approved requester.
      </div>
      <div style={{ width: '100%', maxWidth: 340, textAlign: 'left', marginTop: 4 }}>
        <TextField
          variant="wedge-auth"
          authMotion="instant"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Requester's email"
          autoCapitalize="off"
          style={{ marginBottom: 10 }}
        />
        <TextField
          variant="wedge-auth"
          authMotion="instant"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name (optional)"
          style={{ marginBottom: 10 }}
        />
        <TextField
          variant="wedge-auth"
          authMotion="instant"
          type="text"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          placeholder="Company (optional)"
          style={{ marginBottom: 10 }}
        />
        {err && (
          <div
            style={{ color: BRAND.redText, fontSize: 12.5, margin: '2px 0 10px', lineHeight: 1.4 }}
          >
            {err}
          </div>
        )}
        <Button appearance="wedge-auth" authMotion="instant" authHover="none"
          onClick={generate}
          disabled={busy}
        >
          {busy ? 'Generating…' : 'Generate access code'}
        </Button>
      </div>
    </div>
  );
}
